// FILE: src/pages/CashsalesReportPage.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableFooter,
  CircularProgress,
  Alert,
  Stack,
  Autocomplete,
} from "@mui/material";
import API from "../api";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import { formatDate } from "../utils/formatDate";

function CashsalesReportPage() {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // Filters & pagination state
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [accountFilter, setAccountFilter] = useState("ALL");
  const [accounts, setAccounts] = useState([]);
  const [reportData, setReportData] = useState({ rows: [], pagination: {} });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);

  // Default date range: current month
  const defaultDateRange = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
      start: firstDay.toISOString().split("T")[0],
      end: lastDay.toISOString().split("T")[0],
    };
  };

  useEffect(() => {
    const { start, end } = defaultDateRange();
    setStartDate(start);
    setEndDate(end);
  }, []);

  // Fetch accounts once
  useEffect(() => {
    API.get("/accounts", { params: { limit: 1000000 } })
      .then(res => setAccounts(res.data.accounts || []))
      .catch(err => {
        console.error("Error fetching accounts:", err);
        setError(err.response?.data?.message || "Error fetching accounts");
      });
  }, []);

  // Fetch or append report pages
  const generateReport = async (pageNumber = 1, append = false) => {
    setLoading(true);
    setError("");
    try {
      const params = {
        startDate,
        endDate,
        account: accountFilter,
        page: pageNumber,
        limit: 50,
      };
      const res = await API.get("/advance-reports/cashsales", { params });
      setReportData(prev =>
        append
          ? {
              ...res.data,
              rows: [...prev.rows, ...res.data.rows],
            }
          : res.data
      );
      setPage(res.data.pagination?.currentPage || pageNumber);
    } catch (err) {
      console.error("Error generating report:", err);
      setError(err.response?.data?.message || "Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = () => generateReport(1);
  const handleLoadMore = () => {
    if (page < reportData.pagination.totalPages) {
      generateReport(page + 1, true);
    }
  };

  // CSV Export
  const handleExportCSV = async () => {
    try {
      const token = localStorage.getItem("token");
      const queryParams = `?startDate=${startDate}&endDate=${endDate}&account=${accountFilter}`;
      const response = await API.get(
        `/advance-reports/cashsales/export${queryParams}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "cashsales-report.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Error exporting CSV:", err);
      setError(err.response?.data?.message || "Error exporting CSV");
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Typography variant="h4" sx={{ mb: 3, textAlign: "center" }}>
        Cash Sales Report
      </Typography>

      {/* Filter Panel */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Autocomplete
            options={accounts}
            getOptionLabel={opt => `${opt.accountName} (${opt.accountNo})`}
            value={
              accountFilter === "ALL"
                ? { _id: "ALL", accountName: "All Accounts" }
                : accounts.find(a => a._id === accountFilter) || null
            }
            onChange={(e, nv) => setAccountFilter(nv ? nv._id : "ALL")}
            renderInput={params => <TextField {...params} label="Account" />}
            ListboxProps={{ style: { maxHeight: 240 } }}
            sx={{ width: 250 }}
          />
          <Button variant="contained" onClick={handleGenerateReport}>
            Generate Report
          </Button>
          <Button variant="outlined" onClick={handleExportCSV}>
            Export to CSV
          </Button>
        </Stack>
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption">
            Date Range: {startDate} to {endDate}
          </Typography>
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          {/* Data Table */}
          <Paper sx={{ overflowX: "auto", mb: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell>Cash Sales</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Shift Manager</TableCell>
                  <TableCell>Till Users</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.rows.map((row, idx) => (
                  <TableRow
                    key={idx}
                    sx={
                      row.isSummary
                        ? { fontWeight: "bold", backgroundColor: "#f0f0f0" }
                        : {}
                    }
                  >
                    <TableCell>{formatDate(row.date)}</TableCell>
                    <TableCell>{row.account}</TableCell>
                    <TableCell>{row.cashSales}</TableCell>
                    <TableCell>{row.cashSalesDescription}</TableCell>
                    <TableCell>{row.shiftManager}</TableCell>
                    <TableCell>{row.tillUsers}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={6} />
                </TableRow>
              </TableFooter>
            </Table>
          </Paper>

          {/* Load More Button */}
          <Box sx={{ textAlign: "center", mt: 2 }}>
            {page < reportData.pagination.totalPages && (
              <Button variant="contained" onClick={handleLoadMore} disabled={loading}>
                {loading ? "Loading…" : "Load More"}
              </Button>
            )}
          </Box>
        </>
      )}
    </Box>
  );
}

export default CashsalesReportPage;
